// src/admin/pages/GameDetails.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { obtenerJuegoPorId, actualizarJuego, eliminarJuego } from '../../api';
import ImageUploader from '../components/ImageUploader';

const tabs = ['IMAGE', 'DETAILS', 'DESCRIPTION'];

const GameDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('IMAGE');

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const data = await obtenerJuegoPorId(id);
        setGame(data);
      } catch (error) {
        console.error('Error al cargar juego:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGame();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setGame((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      await actualizarJuego(id, game);
      navigate('/admin/games');
    } catch (error) {
      console.error('Error al guardar juego:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Seguro que quieres eliminar este juego?')) return;
    try {
      await eliminarJuego(id);
      navigate('/admin/games');
    } catch (error) {
      console.error('Error al eliminar juego:', error);
    }
  };

  if (loading) return <div className="p-8 text-white">Loading...</div>;
  if (!game) return <div className="p-8 text-white">Game not found</div>;

  return (
    <div className="p-8 bg-[rgb(var(--color-bg))] min-h-screen text-[rgb(var(--color-text))] font-inter">
      <h1 className="text-3xl font-audiowide mb-6">Edit Game</h1>

      <div className="flex border-b border-[rgb(var(--color-border))] mb-6">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition ${
              activeTab === tab
                ? 'text-blue-400 border-b-2 border-blue-400'
                : 'text-[rgb(var(--color-text))]/70 hover:text-blue-300'
            }`}
          >
            {tab === 'IMAGE' && '📷'}
            {tab === 'DETAILS' && '📐'}
            {tab === 'DESCRIPTION' && '📝'}
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'IMAGE' && (
        <div className="space-y-6 max-w-3xl">
          {game.imagen_url && (
            <div className="relative">
              <label className="block mb-2 text-sm text-[rgb(var(--color-text))]/80">Imagen actual</label>
              <img
                src={game.imagen_url}
                alt="Imagen actual"
                className="aspect-video w-full object-cover rounded-xl border border-[rgb(var(--color-border))]"
              />
              <button
                onClick={() => setGame((prev) => ({ ...prev, imagen_url: '' }))}
                className="absolute top-2 right-2 px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-500"
              >
                Quitar imagen
              </button>
            </div>
          )}

          <ImageUploader
            onUpload={(url) => setGame((prev) => ({ ...prev, imagen_url: url }))}
            onReset={() => setGame((prev) => ({ ...prev, imagen_url: '' }))}
          />
        </div>
      )}

      {activeTab === 'DETAILS' && (
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm mb-1">Name</label>
            <input
              name="nombre"
              value={game.nombre}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Category ID</label>
            <input
              name="category_id"
              value={game.category_id}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
        </div>
      )}

      {activeTab === 'DESCRIPTION' && (
        <div className="grid md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm mb-1">Description</label>
            <textarea
              name="descripcion"
              value={game.descripcion}
              onChange={handleChange}
              className="w-full h-32 p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Duration (min)</label>
            <input
              name="duracion"
              value={game.duracion}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Base Price (₡)</label>
            <input
              name="precio_base"
              value={game.precio_base}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Extra price per player (₡)</label>
            <input
              name="precio_extra_por_jugador"
              value={game.precio_extra_por_jugador}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Base capacity</label>
            <input
              name="capacidad_base"
              value={game.capacidad_base}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
          <div>
            <label className="block text-sm mb-1">Maximum capacity</label>
            <input
              name="capacidad_maxima"
              value={game.capacidad_maxima}
              onChange={handleChange}
              className="w-full p-2 bg-hover-bg border border-[rgb(var(--color-border))] rounded"
            />
          </div>
        </div>
      )}

      <div className="flex justify-between mt-10">
        <button onClick={handleDelete} className="text-red-500 hover:underline">
          🗑 Delete
        </button>
        <div className="space-x-3">
          <button
            onClick={() => navigate('/admin/games')}
            className="px-4 py-2 bg-gray-600 rounded hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-500 text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default GameDetails;